import { type OperatorFunction, of, timeout } from 'rxjs';

/**
 * How long a wait for an answer stays open before it expires.
 *
 * Provisional. No p50/p95 exists for any awaited path, so the number is set
 * above the slowest chain the product has — a cold-start callable plus a dozen
 * serial round trips — and tightens once measured. The asymmetry is what fixes
 * it high: a bound below the real distribution tells someone their answer
 * failed while it is still on its way, and no bound at all tells them nothing
 * at all, ever.
 */
export const WAIT_EXPIRES_AFTER_MS = 90_000;

/**
 * The bound elapsed with no answer.
 *
 * `ok: false` because the thing the person asked for did not happen, which is
 * what every result on these channels means by it — so a surface that already
 * renders `!ok` renders this without being taught anything. `expired`
 * separates it from the refusal it would otherwise be read as: a refusal is an
 * answer, and this is the absence of one, which a retry can still resolve.
 *
 * It carries nothing else. A surface reading the answer's own fields inside
 * its failure branch therefore stops compiling until it says what it does with
 * an expiry, which is the point: the compiler holds the list of surfaces that
 * still owe that answer.
 */
export interface WaitExpired {
  readonly ok: false;
  readonly expired: true;
}

const EXPIRED: WaitExpired = { ok: false, expired: true };

/**
 * Tells an expired wait from an answered one — the distinction a surface makes
 * to offer a retry where it would otherwise report a refusal.
 */
export function isWaitExpired(result: unknown): result is WaitExpired {
  return (
    typeof result === 'object' && result !== null && 'expired' in result && result.expired === true
  );
}

/**
 * Bounds a wait for one answer, and makes its expiry an emitted value.
 *
 * The caller's promise settles and its subscriber's `next` fires either way,
 * so a form that dims on dispatch always un-dims. `timeout` drops the wait
 * when it expires, which is correct here and only here — this wait is over
 * either way, unlike a listener, which is meant to stay open (`silenceAfter`).
 *
 * The bound is read against the first emission only, which is the whole wait:
 * every caller narrows to the one answer it is owed — `take(1)`, or a `filter`
 * on the phase it is waiting for — before bounding it.
 */
export function expireAfter<T>(
  afterMs: number = WAIT_EXPIRES_AFTER_MS,
): OperatorFunction<T, T | WaitExpired> {
  return (source) => source.pipe(timeout({ first: afterMs, with: () => of(EXPIRED) }));
}
